import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Percent,
  Tag,
  TrendingUp,
  DollarSign,
  UtensilsCrossed,
  Users,
  Receipt,
  Clock,
  Banknote,
  Calculator,
  CreditCard,
  FileText,
  Building2,
  RefreshCw,
  LineChart,
  ArrowRightLeft,
  Home,
  Calendar,
  TrendingDown,
  PiggyBank,
  Wallet,
  UserCheck,
  Flame,
  ArrowRight,
  Globe,
  Star,
  Sparkles,
  X,
  SlidersHorizontal,
  Bookmark,
  Check,
  LayoutGrid,
  Landmark,
  Layers,
  HelpCircle,
} from 'lucide-react';
import { ToolCategory, ToolItem, RegionInfo, AppView } from '../../types';
import { getIdentityState, toggleFavorite, subscribeIdentityState } from '../../services/identityEngine';

interface AllToolsViewProps {
  onSelectTool: (toolId: string) => void;
  onNavigate: (view: AppView) => void;
  tools: ToolItem[];
  allRegions: RegionInfo[];
  selectedRegion: RegionInfo;
  onOpenAuthModal?: () => void;
}

export const AllToolsView: React.FC<AllToolsViewProps> = ({
  onSelectTool,
  onNavigate,
  tools,
  allRegions,
  selectedRegion,
  onOpenAuthModal,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ToolCategory | 'All'>('All');
  const [selectedMarket, setSelectedMarket] = useState<string>('All');
  const [identityState, setIdentityState] = useState(() => getIdentityState());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Subscribe to identity updates for instant reactive favorites
  useEffect(() => {
    const unsub = subscribeIdentityState((state) => {
      setIdentityState(state);
    });
    return () => unsub();
  }, []);

  const isLoggedIn = !identityState.user.isGuest;

  const categories: { id: ToolCategory | 'All'; label: string; icon: React.FC<{ className?: string }> }[] = [
    { id: 'All', label: 'All Tools', icon: LayoutGrid },
    { id: 'Everyday Money', label: 'Everyday Money', icon: Wallet },
    { id: 'Income & Tax', label: 'Income & Tax', icon: Landmark },
    { id: 'Currency & Transfers', label: 'Currency & Transfers', icon: RefreshCw },
    { id: 'Borrow', label: 'Borrow', icon: Building2 },
    { id: 'Save & Invest', label: 'Save & Invest', icon: TrendingUp },
  ];

  // Helper to render tool icon based on iconName
  const renderToolIcon = (iconName: string, className = 'w-5 h-5') => {
    switch (iconName) {
      case 'Percent':
        return <Percent className={className} />;
      case 'Tag':
        return <Tag className={className} />;
      case 'TrendingUp':
        return <TrendingUp className={className} />;
      case 'DollarSign':
        return <DollarSign className={className} />;
      case 'UtensilsCrossed':
        return <UtensilsCrossed className={className} />;
      case 'Users':
        return <Users className={className} />;
      case 'Receipt':
        return <Receipt className={className} />;
      case 'Clock':
        return <Clock className={className} />;
      case 'Banknote':
        return <Banknote className={className} />;
      case 'Calculator':
        return <Calculator className={className} />;
      case 'CreditCard':
        return <CreditCard className={className} />;
      case 'FileText':
        return <FileText className={className} />;
      case 'Building2':
        return <Building2 className={className} />;
      case 'RefreshCw':
        return <RefreshCw className={className} />;
      case 'LineChart':
        return <LineChart className={className} />;
      case 'ArrowRightLeft':
        return <ArrowRightLeft className={className} />;
      case 'Home':
        return <Home className={className} />;
      case 'Calendar':
        return <Calendar className={className} />;
      case 'TrendingDown':
        return <TrendingDown className={className} />;
      case 'PiggyBank':
        return <PiggyBank className={className} />;
      case 'Wallet':
        return <Wallet className={className} />;
      case 'UserCheck':
        return <UserCheck className={className} />;
      case 'Flame':
        return <Flame className={className} />;
      default:
        return <Calculator className={className} />;
    }
  };

  // Instant multi-attribute search & filter
  const filteredTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return tools.filter((tool) => {
      // 1. Strict Category Isolation Rule
      if (selectedCategory !== 'All') {
        if (tool.category !== selectedCategory) return false;
      }

      // 2. Market / Jurisdiction filter
      if (selectedMarket !== 'All') {
        const supportsGlobal = tool.regionsSupported.includes('Global') || (tool.marketSupport && tool.marketSupport.includes('Global'));
        const supportsSpecific = tool.regionsSupported.includes(selectedMarket) || (tool.marketSupport && tool.marketSupport.includes(selectedMarket));
        if (!supportsGlobal && !supportsSpecific) return false;
      }

      // 3. Search query matching across name, alias, slug, tagline, subtitle, description, category, and keywords
      if (query) {
        const nameMatch = tool.name.toLowerCase().includes(query);
        const labelMatch = tool.label ? tool.label.toLowerCase().includes(query) : false;
        const slugMatch = tool.slug.toLowerCase().includes(query);
        const subtitleMatch = tool.subtitle ? tool.subtitle.toLowerCase().includes(query) : false;
        const taglineMatch = tool.tagline ? tool.tagline.toLowerCase().includes(query) : false;
        const descMatch = tool.description.toLowerCase().includes(query);
        const catMatch = tool.category.toLowerCase().includes(query);
        const inputLabelsMatch = tool.inputs?.some((inp) => inp.label.toLowerCase().includes(query));

        return nameMatch || labelMatch || slugMatch || subtitleMatch || taglineMatch || descMatch || catMatch || inputLabelsMatch;
      }

      return true;
    });
  }, [tools, selectedCategory, selectedMarket, searchQuery]);

  // Check if a tool is favorited
  const isToolFavorited = (toolId: string) => {
    return identityState.favorites.some((f) => f.targetId === toolId || f.targetId === `tool_${toolId}`);
  };

  // Handle favorite action
  const handleToggleFavorite = (e: React.MouseEvent, tool: ToolItem) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      if (onOpenAuthModal) {
        onOpenAuthModal();
      } else {
        showToast('Sign in to save tools to your YamooPay Workspace');
      }
      return;
    }

    toggleFavorite('TOOL', tool.id, tool.name, tool.subtitle || tool.category);
    const nowFav = !isToolFavorited(tool.id);
    showToast(nowFav ? `Saved ${tool.name} to your Workspace` : `Removed ${tool.name} from Favorites`);
  };

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3200);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedMarket('All');
  };

  // Popular tools for the top highlight cards
  const popularHighlightTools = useMemo(() => {
    return [
      tools.find((t) => t.id === 'compound-interest-calculator') || tools.find((t) => t.category === 'Save & Invest'),
      tools.find((t) => t.id === 'currency-converter') || tools.find((t) => t.category === 'Currency & Transfers'),
    ].filter(Boolean) as ToolItem[];
  }, [tools]);

  // Group filtered tools by canonical category for 'All' view
  const categoryOrder: ToolCategory[] = [
    'Everyday Money',
    'Income & Tax',
    'Currency & Transfers',
    'Borrow',
    'Save & Invest',
  ];

  const toolsByCategory = useMemo(() => {
    const map = new Map<ToolCategory, ToolItem[]>();
    categoryOrder.forEach((cat) => map.set(cat, []));

    filteredTools.forEach((tool) => {
      const list = map.get(tool.category) || [];
      list.push(tool);
      map.set(tool.category, list);
    });

    return map;
  }, [filteredTools]);

  return (
    <div className="w-full bg-[#FBFBFC] min-h-[calc(100vh-4rem)]">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#1A1A1A] text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-xs font-semibold animate-in fade-in slide-in-from-bottom-3 duration-200">
          <Check className="w-4 h-4 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Main Content Layout with Responsive Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
          
          {/* =================================================================== */}
          {/* 1. LEFT SIDEBAR NAVIGATION (Desktop) & SCROLLABLE BAR (Mobile)     */}
          {/* =================================================================== */}
          <aside className="w-full lg:w-64 shrink-0" aria-label="Tool Categories Filter">
            {/* Desktop Vertical Menu */}
            <div className="hidden lg:block sticky top-24 space-y-1">
              <nav className="space-y-1" role="navigation" aria-label="Categories">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = selectedCategory === cat.id;
                  const count =
                    cat.id === 'All'
                      ? tools.length
                      : tools.filter((t) => t.category === cat.id).length;

                  return (
                    <button
                      key={cat.id}
                      id={`category-tab-${cat.id.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                        isActive
                          ? 'bg-[#EFF6FF] text-[#2563EB] shadow-xs'
                          : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-4 h-4 ${isActive ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                        <span>{cat.label}</span>
                      </div>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full font-mono font-medium ${
                          isActive ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#F3F4F6] text-[#9CA3AF]'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Discovery Wizard Prompt */}
              <div className="pt-6 mt-6 border-t border-[#E5E7EB]">
                <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-[#1A1A1A]">
                    <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                    <span>Need Guidance?</span>
                  </div>
                  <p className="text-[11px] text-[#6B7280] leading-relaxed">
                    Unsure which calculator matches your financial situation? Try the interactive Discovery Wizard.
                  </p>
                  <button
                    onClick={() => onNavigate('discovery')}
                    className="w-full mt-2 py-1.5 px-3 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl text-[11px] font-bold transition-colors flex items-center justify-center gap-1.5"
                  >
                    <span>Launch Wizard</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Category Horizontal Pills */}
            <div className="lg:hidden w-full overflow-x-auto pb-2 flex items-center gap-1.5 no-scrollbar">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap shrink-0 transition-all ${
                      isActive
                        ? 'bg-[#1A1A1A] text-white shadow-xs'
                        : 'bg-white text-[#4B5563] border border-[#E5E7EB] hover:bg-[#F3F4F6]'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-[#6B7280]'}`} />
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* =================================================================== */}
          {/* 2. MAIN DIRECTORY CONTENT                                          */}
          {/* =================================================================== */}
          <main className="flex-1 w-full space-y-8" role="main">
            {/* Page Header */}
            <header className="space-y-3">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
                All Financial Tools
              </h1>
              <p className="text-sm sm:text-base text-[#6B7280] max-w-2xl leading-relaxed">
                Discover our comprehensive suite of financial calculators and tools designed to simplify your complex financial decisions.
              </p>
            </header>

            {/* Large Search Input & Market Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              {/* Search Bar */}
              <div className="relative flex-1" role="search">
                <Search className="w-4 h-4 text-[#9CA3AF] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="tools-search-input"
                  type="text"
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search tools by name, formula, or keyword"
                  className="w-full pl-11 pr-10 py-3.5 bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] focus:border-[#2563EB] rounded-2xl text-sm font-medium text-[#1A1A1A] placeholder-[#9CA3AF] outline-none transition-all shadow-xs"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search input"
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Market Filter */}
              <div className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-2xl px-3 py-2 shrink-0 shadow-xs">
                <Globe className="w-4 h-4 text-[#6B7280]" />
                <select
                  id="market-jurisdiction-filter"
                  value={selectedMarket}
                  onChange={(e) => setSelectedMarket(e.target.value)}
                  aria-label="Filter tools by target jurisdiction or currency support"
                  className="bg-transparent text-xs font-semibold text-[#1A1A1A] outline-none cursor-pointer pr-2"
                >
                  <option value="All">All Markets (Global)</option>
                  {allRegions.map((reg) => (
                    <option key={reg.code} value={reg.code}>
                      {reg.flag} {reg.name} ({reg.currency})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active Filters Summary if searching or filtered */}
            {(searchQuery.trim() || selectedCategory !== 'All' || selectedMarket !== 'All') && (
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs pt-1 pb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#6B7280]">
                    Showing <strong className="text-[#1A1A1A]">{filteredTools.length}</strong> of {tools.length} verified tools
                  </span>
                  {searchQuery.trim() && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] text-[#2563EB] rounded-lg font-medium">
                      Query: "{searchQuery}"
                      <button onClick={() => setSearchQuery('')} className="hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {selectedCategory !== 'All' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#F3F4F6] text-[#1A1A1A] rounded-lg font-medium">
                      Category: {selectedCategory}
                      <button onClick={() => setSelectedCategory('All')} className="hover:text-black">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {selectedMarket !== 'All' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#F3F4F6] text-[#1A1A1A] rounded-lg font-medium">
                      Market: {selectedMarket}
                      <button onClick={() => setSelectedMarket('All')} className="hover:text-black">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>

                <button
                  onClick={handleClearFilters}
                  className="text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8] underline underline-offset-2"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* =================================================================== */}
            {/* 3. POPULAR TOOLS SECTION (Visible when All selected & no search)   */}
            {/* =================================================================== */}
            {selectedCategory === 'All' && !searchQuery.trim() && selectedMarket === 'All' && (
              <section className="space-y-4" aria-labelledby="popular-tools-heading">
                <h2 id="popular-tools-heading" className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                  Popular Tools
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {popularHighlightTools.map((tool) => {
                    const isFav = isToolFavorited(tool.id);
                    const isCompound = tool.id.includes('compound');

                    return (
                      <article
                        key={tool.id}
                        id={`popular-tool-card-${tool.slug}`}
                        onClick={() => onSelectTool(tool.id)}
                        className="relative bg-white border border-[#E5E7EB] hover:border-[#2563EB] rounded-2xl p-6 shadow-xs hover:shadow-md transition-all cursor-pointer overflow-hidden group flex flex-col justify-between"
                      >
                        {/* Decorative subtle gradient bubble in corner matching visual reference */}
                        <div
                          className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-40 pointer-events-none ${
                            isCompound ? 'bg-blue-200' : 'bg-emerald-200'
                          }`}
                        />

                        <div className="relative space-y-3">
                          <div className="flex items-center justify-between">
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                isCompound ? 'bg-[#EFF6FF] text-[#2563EB]' : 'bg-[#ECFDF5] text-[#059669]'
                              }`}
                            >
                              {renderToolIcon(tool.iconName, 'w-5 h-5')}
                            </div>

                            <button
                              onClick={(e) => handleToggleFavorite(e, tool)}
                              aria-label={isFav ? `Remove ${tool.name} from favorites` : `Save ${tool.name} to favorites`}
                              title={isLoggedIn ? (isFav ? 'Remove from Workspace' : 'Save to Workspace') : 'Sign in to save'}
                              className={`p-2 rounded-xl border transition-all ${
                                isFav
                                  ? 'bg-amber-50 text-amber-500 border-amber-200'
                                  : 'bg-white text-[#9CA3AF] hover:text-[#1A1A1A] border-[#E5E7EB] hover:bg-[#F9FAFB]'
                              }`}
                            >
                              <Bookmark className={`w-4 h-4 ${isFav ? 'fill-amber-500' : ''}`} />
                            </button>
                          </div>

                          <div>
                            <h3 className="text-base font-bold text-[#1A1A1A] group-hover:text-[#2563EB] transition-colors">
                              {tool.name} {tool.name.includes('Calculator') || tool.name.includes('Converter') ? '' : 'Calculator'}
                            </h3>
                            <p className="text-xs text-[#6B7280] mt-1.5 leading-relaxed">
                              {tool.description}
                            </p>
                          </div>
                        </div>

                        <div className="relative pt-4 mt-3 border-t border-[#F3F4F6] flex items-center justify-between text-xs">
                          <span className="text-[11px] font-medium text-[#9CA3AF] flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {tool.regionsSupported.includes('Global') ? 'Global Multi-Currency' : `${tool.regionsSupported.length} Jurisdictions`}
                          </span>
                          <span className="text-xs font-bold text-[#2563EB] group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
                            Launch <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {/* =================================================================== */}
            {/* 4. MAIN CATEGORY SECTIONS / RESULTS VIEW                           */}
            {/* =================================================================== */}
            {filteredTools.length > 0 ? (
              <div className="space-y-10">
                {/* When viewing All without search: Show each category grouped */}
                {selectedCategory === 'All' && !searchQuery.trim() ? (
                  categoryOrder.map((catName) => {
                    const catTools = toolsByCategory.get(catName) || [];
                    if (catTools.length === 0) return null;

                    return (
                      <section key={catName} className="space-y-4" aria-labelledby={`cat-heading-${catName.replace(/[^a-z0-9]/gi, '-')}`}>
                        <div className="flex items-center justify-between">
                          <h2
                            id={`cat-heading-${catName.replace(/[^a-z0-9]/gi, '-')}`}
                            className="text-xl font-bold text-[#1A1A1A] tracking-tight"
                          >
                            {catName}
                          </h2>
                          <button
                            onClick={() => setSelectedCategory(catName)}
                            className="text-xs font-semibold text-[#6B7280] hover:text-[#2563EB] transition-colors"
                          >
                            View all {catTools.length} tools →
                          </button>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {catTools.map((tool) => (
                            <ToolCard
                              key={tool.id}
                              tool={tool}
                              onSelectTool={onSelectTool}
                              isFavorited={isToolFavorited(tool.id)}
                              onToggleFavorite={handleToggleFavorite}
                              renderIcon={renderToolIcon}
                              isLoggedIn={isLoggedIn}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })
                ) : (
                  /* When a specific category is selected OR user is searching */
                  <section className="space-y-4">
                    <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                      <div>
                        <h2 className="text-xl font-bold text-[#1A1A1A] tracking-tight">
                          {selectedCategory === 'All' ? 'Search Results' : selectedCategory}
                        </h2>
                        <p className="text-xs text-[#6B7280] mt-0.5">
                          {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'} available
                        </p>
                      </div>

                      {selectedCategory !== 'All' && (
                        <button
                          onClick={() => setSelectedCategory('All')}
                          className="text-xs font-semibold text-[#2563EB] hover:underline"
                        >
                          ← Back to All Tools
                        </button>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredTools.map((tool) => (
                        <ToolCard
                          key={tool.id}
                          tool={tool}
                          onSelectTool={onSelectTool}
                          isFavorited={isToolFavorited(tool.id)}
                          onToggleFavorite={handleToggleFavorite}
                          renderIcon={renderToolIcon}
                          isLoggedIn={isLoggedIn}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              /* =================================================================== */
              /* 5. EMPTY STATE                                                     */
              /* =================================================================== */
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 text-center space-y-4 shadow-xs">
                <div className="w-12 h-12 rounded-2xl bg-[#F3F4F6] text-[#6B7280] mx-auto flex items-center justify-center">
                  <Search className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-bold text-[#1A1A1A]">No tools match your filters</h3>
                  <p className="text-xs text-[#6B7280] max-w-sm mx-auto leading-relaxed">
                    We couldn't find any financial calculators matching your current search terms or market criteria.
                  </p>
                </div>
                <button
                  id="clear-filters-empty-cta"
                  onClick={handleClearFilters}
                  className="px-5 py-2.5 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl transition-all shadow-xs inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Clear filters</span>
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

/* =================================================================== */
/* INDIVIDUAL TOOL CARD COMPONENT                                      */
/* =================================================================== */

interface ToolCardProps {
  tool: ToolItem;
  onSelectTool: (toolId: string) => void;
  isFavorited: boolean;
  onToggleFavorite: (e: React.MouseEvent, tool: ToolItem) => void;
  renderIcon: (iconName: string, className?: string) => React.ReactNode;
  isLoggedIn: boolean;
}

const ToolCard: React.FC<ToolCardProps> = ({
  tool,
  onSelectTool,
  isFavorited,
  onToggleFavorite,
  renderIcon,
  isLoggedIn,
}) => {
  return (
    <article
      id={`tool-card-${tool.slug}`}
      onClick={() => onSelectTool(tool.id)}
      className="bg-white border border-[#E5E7EB] hover:border-[#2563EB] rounded-2xl p-5 shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col justify-between group space-y-3"
    >
      <div className="space-y-3">
        {/* Card Header: Icon + Badges + Favorite */}
        <div className="flex items-center justify-between">
          <div className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#F1F5F9] group-hover:bg-[#EFF6FF] group-hover:border-[#DBEAFE] text-[#475569] group-hover:text-[#2563EB] flex items-center justify-center transition-colors">
            {renderIcon(tool.iconName, 'w-4 h-4')}
          </div>

          <div className="flex items-center gap-1.5">
            {tool.popular && (
              <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md">
                Popular
              </span>
            )}
            <button
              onClick={(e) => onToggleFavorite(e, tool)}
              aria-label={isFavorited ? `Remove ${tool.name} from favorites` : `Save ${tool.name} to favorites`}
              title={isLoggedIn ? (isFavorited ? 'Remove from Workspace' : 'Save to Workspace') : 'Sign in to save'}
              className={`p-1.5 rounded-lg border transition-all ${
                isFavorited
                  ? 'bg-amber-50 text-amber-500 border-amber-200'
                  : 'bg-[#F9FAFB] text-[#9CA3AF] hover:text-[#1A1A1A] border-[#E5E7EB] hover:bg-white'
              }`}
            >
              <Bookmark className={`w-3.5 h-3.5 ${isFavorited ? 'fill-amber-500' : ''}`} />
            </button>
          </div>
        </div>

        {/* Card Body: Title + One-line Purpose */}
        <div>
          <h3 className="text-sm font-bold text-[#1A1A1A] group-hover:text-[#2563EB] transition-colors line-clamp-1">
            {tool.name}
          </h3>
          <p className="text-xs text-[#6B7280] mt-1 line-clamp-2 leading-relaxed">
            {tool.description}
          </p>
        </div>
      </div>

      {/* Card Footer: Market availability tag & Arrow */}
      <div className="pt-3 border-t border-[#F3F4F6] flex items-center justify-between text-[11px]">
        <span className="text-[#9CA3AF] font-medium truncate max-w-[150px]">
          {tool.regionsSupported.includes('Global')
            ? 'Global'
            : tool.regionsSupported.join(', ')}
        </span>
        <span className="font-bold text-[#2563EB] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all flex items-center gap-1">
          Open <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </article>
  );
};
